import { useQuery } from "@tanstack/react-query";

interface DFlowMint {
  yesMint: string;
  noMint: string;
}

/** Batch fetch DFlow mints for multiple tickers (event page) */
async function fetchDFlowMintsEvent(tickers: string[]): Promise<Record<string, DFlowMint>> {
  const res = await fetch("/api/dflow/markets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tickers }),
  });
  const data = await res.json();
  if (!data.success || !Array.isArray(data.data)) return {};
  const map: Record<string, DFlowMint> = {};
  for (const m of data.data) {
    if (m.ticker && m.accounts?.yesMint && m.accounts?.noMint) {
      map[m.ticker] = { yesMint: m.accounts.yesMint, noMint: m.accounts.noMint };
    }
  }
  return map;
}

/** Single fetch DFlow mints for one ticker (market page) */
async function fetchDFlowMintsSingle(ticker: string): Promise<{ mints: DFlowMint | null; available: boolean }> {
  const res = await fetch(`/api/dflow/markets?ticker=${encodeURIComponent(ticker.toUpperCase())}`);
  const data = await res.json();
  if (data.success && Array.isArray(data.data) && data.data.length > 0) {
    const match = data.data[0];
    if (match?.accounts?.yesMint && match?.accounts?.noMint) {
      return {
        mints: { yesMint: match.accounts.yesMint, noMint: match.accounts.noMint },
        available: true,
      };
    }
  }
  return { mints: null, available: false };
}

/**
 * React Query hook for batch DFlow mints (event page with multiple markets).
 * Only enabled for Kalshi events with market tickers.
 */
export function useDFlowMintsEvent(tickers: string[], enabled: boolean) {
  return useQuery<Record<string, DFlowMint>>({
    queryKey: ["dflow-mints", "event", ...tickers.slice().sort()],
    queryFn: () => fetchDFlowMintsEvent(tickers),
    enabled: enabled && tickers.length > 0,
    staleTime: 2 * 60_000,  // 2min
    gcTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * React Query hook for single DFlow mint (market page).
 * Only enabled for Kalshi markets.
 */
export function useDFlowMintsSingle(ticker: string, isKalshi: boolean) {
  return useQuery<{ mints: DFlowMint | null; available: boolean }>({
    queryKey: ["dflow-mints", "market", ticker],
    queryFn: () => fetchDFlowMintsSingle(ticker),
    enabled: isKalshi && !!ticker,
    staleTime: 2 * 60_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
